import { Key, SyntheticEvent, useMemo } from 'react'

import { useDebounceFn } from 'ahooks'
import { Transfer } from 'antd'
import { createStyles } from 'antd-style'
import { difference, differenceBy, isNil } from 'lodash'

import { GenericalRecord } from '../../../types/utilities'
import { isScrollEnd } from '../../../utils/dom'
import { pick } from '../../../utils/object'
import { DataEmpty } from '../../DataEmpty'
import { DataError } from '../../DataError'
import { ProTable } from '../../ProTable'
import { ProTableProps } from '../../ProTable/interface'
import { RemoteTransferLeftPanel, RemoteTransferRightPanel } from '../Panel'
import { RemoteTransferSearchForm } from '../SearchForm'
import { useRemoteTransferStore } from '../Store/hooks/useStore'
import { getKey } from '../utils/getKey'

import { RemoteTransferCoreProps, TransferRecord } from './interface'

const DEFAULT_TITLES = ['待选择', '已选择']

const DEFAULT_LIST_STYLE = {
  overflow: 'hidden'
}

const useStyles = createStyles(({ prefixCls, token }) => ({
  table: {
    position: 'relative',
    overflow: 'auto',
    [`.${prefixCls}-table-thead`]: {
      position: 'sticky',
      top: 0,
      zIndex: token.zIndexPopupBase
    },
    [`.${prefixCls}-table-placeholder>.${prefixCls}-table-cell`]: {
      border: 'none'
    }
  },
  row: {
    cursor: 'pointer'
  }
}))

export const TableTransferCore = <
  T extends TransferRecord,
  S = GenericalRecord
>({
  /* -------------------------------------------------------------------------- */
  /*                               Transfer Props                               */
  /* -------------------------------------------------------------------------- */
  className,
  style,
  styles: externalStyles = {},
  disabled,
  oneWay,
  titles = DEFAULT_TITLES,
  rightFilterOption,
  showSearch = true,

  /* -------------------------------------------------------------------------- */
  /*                                Customize Props                             */
  /* -------------------------------------------------------------------------- */
  rowKey,
  height,
  leftSearchForm,
  leftColumns,
  rightColumns,
  leftFooter,
  rightFooter
}: RemoteTransferCoreProps<T, S>) => {
  const {
    value,
    setValue,
    allData,
    error,
    loading,
    reload,
    fetchNextPage,
    fetchSearch
  } = useRemoteTransferStore(state =>
    pick(state, [
      'searchForm',
      'value',
      'setValue',
      'allData',
      'data',
      'error',
      'loading',
      'reload',
      'fetchNextPage',
      'fetchSearch'
    ])
  )

  const selectedKeys = useMemo(
    () => value.map(item => getKey(item, rowKey)),
    [rowKey, value]
  )

  const allList = useMemo(() => {
    // 避免搜索后已选目标被重置
    const diffrentValue = differenceBy(
      Object.values(allData),
      value,
      `${rowKey}`
    )

    return diffrentValue.concat(value)
  }, [allData, rowKey, value])

  const leftLocale = {
    emptyText: error ? (
      <DataError error={error} reload={reload} />
    ) : selectedKeys.length ? undefined : (
      <DataEmpty reload={reload} />
    )
  }

  const handleChange = (nextTargetKeys: Key[]) => {
    const newValues = allList.filter(item =>
      nextTargetKeys.includes(getKey(item, rowKey))
    )
    setValue(newValues)
    // 全选之后自动触发一次请求
    if (newValues.length === allList.length) {
      fetchNextPage()
    }
  }

  const handleScroll = (e: SyntheticEvent<HTMLElement>) => {
    if (isScrollEnd(e.currentTarget)) {
      fetchNextPage()
    }
  }

  const handleFilter = (searchValue: string, record: T, direction: string) => {
    return direction === 'left'
      ? true
      : (rightFilterOption?.(searchValue, record) ?? true)
  }

  const { run: handleSearch } = useDebounceFn(
    (direction: string, searchValue: string) => {
      if (direction === 'left') {
        fetchSearch({ search: searchValue })
      }
    },
    {
      leading: false,
      trailing: true,
      wait: 300
    }
  )

  const { styles } = useStyles()

  const computedStyles =
    typeof externalStyles === 'function'
      ? info => {
          const ext = externalStyles(info) ?? {}
          return {
            section: { ...DEFAULT_LIST_STYLE, ...ext.section },
            actions: ext.actions
          }
        }
      : {
          section: { ...DEFAULT_LIST_STYLE, ...(externalStyles.section ?? {}) },
          actions: externalStyles.actions
        }

  return (
    <Transfer
      className={className}
      dataSource={allList}
      disabled={disabled}
      filterOption={handleFilter}
      footer={undefined}
      oneWay={oneWay}
      rowKey={record => getKey(record, rowKey)}
      showSearch={showSearch}
      showSelectAll={false}
      style={style}
      styles={computedStyles}
      targetKeys={selectedKeys}
      titles={titles}
      onChange={handleChange}
      onSearch={handleSearch}
    >
      {({
        direction,
        filteredItems,
        onItemSelectAll,
        onItemSelect,
        selectedKeys: listSelectedKeys,
        disabled: listDisabled
      }) => {
        const rowSelection: ProTableProps<T, S>['rowSelection'] = {
          getCheckboxProps: item => ({
            disabled: item.disabled || listDisabled
          }),
          onSelectAll(selected, selectedRows) {
            const tableSelectedKeys = selectedRows
              .filter(item => !item.disabled)
              .map(item => getKey(item, rowKey))
            const diffKeys = selected
              ? difference(tableSelectedKeys, listSelectedKeys)
              : difference(listSelectedKeys, tableSelectedKeys)
            onItemSelectAll(diffKeys, selected)
          },
          onSelect(item, selected) {
            onItemSelect(getKey(item, rowKey), selected)
          },
          selectedRowKeys: listSelectedKeys
        }

        const onRow: ProTableProps<T, S>['onRow'] = record => ({
          onClick() {
            if (record.disabled || listDisabled) {
              return
            }
            const key = getKey(record, rowKey)
            onItemSelect(key, !listSelectedKeys.includes(key))
          }
        })

        return direction === 'left' ? (
          <RemoteTransferLeftPanel>
            {leftSearchForm && <RemoteTransferSearchForm {...leftSearchForm} />}
            <div
              className={styles.table}
              style={{ height }}
              onScroll={handleScroll}
            >
              <ProTable
                columns={leftColumns}
                dataSource={filteredItems}
                loading={loading}
                locale={leftLocale}
                pagination={false}
                rowClassName={styles.row}
                rowKey={rowKey}
                rowSelection={rowSelection}
                size='small'
                onRow={onRow}
              />
            </div>
            {leftFooter}
          </RemoteTransferLeftPanel>
        ) : (
          <RemoteTransferRightPanel
            footer={rightFooter}
            syncHeight={!isNil(height)}
          >
            <ProTable
              columns={rightColumns}
              dataSource={filteredItems}
              pagination={false}
              rootClassName={styles.table}
              rowClassName={styles.row}
              rowKey={rowKey}
              rowSelection={rowSelection}
              size='small'
              onRow={onRow}
            />
          </RemoteTransferRightPanel>
        )
      }}
    </Transfer>
  )
}
